import { Application, Feature } from '@frontend/functionality/types';
import { PackageClient, PackageWorkflowClient } from '@frontend/package/api';
import { Package, PackageStateName, PackageTriggerName, PackageType } from '@frontend/package/types';
import { TransactionClient } from '@frontend/transaction/api';
import { Transaction, TransactionStateName } from '@frontend/transaction/types';

export interface MoveTransactionDestination {
    spotId: string;
    moduleId: string;
    slotId: string;
}

export interface MoveTransactionInput {
    accountId: string;
    transactionId: string;
    transactionState: TransactionStateName;
    transactionUserId?: string | null;
    currentSpotId?: string | null;
    currentSlotId?: string | null;
    destination: MoveTransactionDestination;
    dropOffPackageWorkflowId: string;
    pickUpPackageWorkflowId: string;
    actorUserId?: string;
    triggerLocationSpotId?: string;
}

export interface MoveTransactionResult {
    dropPackage: Package;
    pickPackage: Package;
    transaction: Transaction;
    /** Source pick package skipped on pick-up pending moves (workflow opens the SLOT). */
    openedPackage?: Package;
}

export const isMovableTransactionState = (state: TransactionStateName): boolean =>
    state === TransactionStateName.TRANSACTION_DROP_OFF_PENDING || state === TransactionStateName.TRANSACTION_PICK_UP_PENDING;

/** How many source SLOTs the move confirm UI opens (pick-up pending = 1; otherwise 0). */
export const getMoveOpeningSlotCount = (state: TransactionStateName): number => (state === TransactionStateName.TRANSACTION_PICK_UP_PENDING ? 1 : 0);

export const isActivePackage = (pkg: Package): boolean => {
    const state = pkg.state.toLowerCase();
    return !state.includes('done') && !state.includes('cancelled') && !state.includes('failed') && !state.includes('skipped');
};

const matchesCurrentLocation = (pkg: Package, currentSpotId?: string | null, currentSlotId?: string | null): boolean => {
    if (currentSpotId && pkg.spot_id !== currentSpotId) return false;
    if (currentSlotId && pkg.slot_id !== currentSlotId) return false;
    return true;
};

export const findPendingDropAndPick = (
    packages: Package[],
    currentSpotId?: string | null,
    currentSlotId?: string | null
): { pendingDrop?: Package; pendingPick?: Package } => {
    const active = packages.filter(isActivePackage);
    const atLocation = (pkg: Package) => matchesCurrentLocation(pkg, currentSpotId, currentSlotId);

    const pendingDrop = active.find((pkg) => pkg.type === PackageType.DROP_OFF && atLocation(pkg)) ?? active.find((pkg) => pkg.type === PackageType.DROP_OFF);
    const pendingPick = active.find((pkg) => pkg.type === PackageType.PICK_UP && atLocation(pkg)) ?? active.find((pkg) => pkg.type === PackageType.PICK_UP);

    return { pendingDrop, pendingPick };
};

/**
 * Creates a new drop + pick package chain at the destination, skips the previous
 * packages (workflow may open the source SLOT on pick-up pending), and patches
 * the transaction location.
 */
export async function moveTransaction(input: MoveTransactionInput): Promise<MoveTransactionResult> {
    const {
        accountId,
        transactionId,
        transactionState,
        destination,
        dropOffPackageWorkflowId,
        pickUpPackageWorkflowId,
        transactionUserId,
        actorUserId,
        triggerLocationSpotId,
        currentSpotId,
        currentSlotId
    } = input;

    if (!isMovableTransactionState(transactionState)) {
        throw new Error('Transaction is not in a movable state');
    }

    const response = await PackageClient.fetchAccountTransactionPackages(accountId, transactionId);
    const { pendingDrop, pendingPick } = findPendingDropAndPick(response.results ?? [], currentSpotId, currentSlotId);

    const previousPackageId = transactionState === TransactionStateName.TRANSACTION_PICK_UP_PENDING ? pendingPick?.id : (pendingPick?.id ?? pendingDrop?.id);

    const packageUserId = transactionUserId ?? actorUserId ?? null;
    const sharedPackageFields = {
        spot_id: destination.spotId,
        module_id: destination.moduleId,
        slot_id: destination.slotId,
        quantity: 1,
        state: PackageStateName.PACKAGE_CREATED,
        application: Application.PUDO,
        feature: Feature.MOVE_TRANSACTION,
        user_id: packageUserId,
        blocking: true
    };

    const dropPackage = await PackageClient.postPackage(accountId, transactionId, {
        ...sharedPackageFields,
        type: PackageType.DROP_OFF,
        workflow_id: dropOffPackageWorkflowId,
        dependent_package_id: previousPackageId,
        priority: 4
    });

    const pickPackage = await PackageClient.postPackage(accountId, transactionId, {
        ...sharedPackageFields,
        type: PackageType.PICK_UP,
        workflow_id: pickUpPackageWorkflowId,
        dependent_package_id: dropPackage.id,
        priority: 3
    });

    const triggerMeta = {
        location: triggerLocationSpotId,
        actor: actorUserId
    };

    let openedPackage: Package | undefined;

    if (transactionState === TransactionStateName.TRANSACTION_DROP_OFF_PENDING) {
        if (pendingDrop) {
            await PackageWorkflowClient.triggerPackageState(accountId, transactionId, pendingDrop.id, {
                trigger: PackageTriggerName.PACKAGE_SKIP,
                ...triggerMeta
            });
        }
        if (pendingPick) {
            await PackageWorkflowClient.triggerPackageState(accountId, transactionId, pendingPick.id, {
                trigger: PackageTriggerName.PACKAGE_SKIP,
                ...triggerMeta
            });
        }
    } else {
        if (!pendingPick) {
            throw new Error('No pick-up pending package found to skip');
        }
        await PackageWorkflowClient.triggerPackageState(accountId, transactionId, pendingPick.id, {
            trigger: PackageTriggerName.PACKAGE_SKIP,
            ...triggerMeta
        });
        openedPackage = pendingPick;
    }

    const transaction = await TransactionClient.patchTransaction(accountId, transactionId, {
        spot_id: destination.spotId,
        slot_id: destination.slotId
    });

    return { dropPackage, pickPackage, transaction, openedPackage };
}
